import {TextField} from '@mui/material';
import {
  DateField,
  DateFieldProps,
  DatePicker,
  DatePickerSlotsComponentsProps,
  deDE,
  PickersActionBarAction,
} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import moment, {Moment} from 'moment';
import 'moment/locale/de';
import React, {RefObject} from 'react';
import {useTranslation} from 'react-i18next';

import {convertToMoment} from '@ELib/utilities/temporal';

import {StyledDateTimeTextField} from '../ETextInput';
import {FormInputWrapper} from '../FormInputWrapper';
import {stopKeyboardEventPropagation} from '../keyboardEventPropgation';
import {SharedProps} from '../sharedProps';

import {convertToISODateString, EDateSharedType} from './EDateComponents.utils';

export type EDateInputProps = EDateSharedType &
  SharedProps & {
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    inputOnly?: boolean;
    inputRef?: RefObject<HTMLInputElement>;
    actions?: PickersActionBarAction[];
  };

const germanLocale =
  deDE.components.MuiLocalizationProvider.defaultProps.localeText;
export const EDateInput = ({
  label,
  labelInfo,
  name,
  value,
  dateValue,
  lang = 'de',
  bottomText,
  autoFocus,
  ariaLabel,
  disabled,
  maxDate,
  minDate,
  onChange,
  onDateChanged,
  onFocus,
  onBlur,
  error,
  errorMessage,
  inputOnly = false,
  inputRef,
  actions = ['clear', 'today'],
}: EDateInputProps): React.ReactElement => {
  const {t} = useTranslation('lib');
  const localeTexts = {
    ...(lang === 'de' || lang.startsWith('de_') ? germanLocale : {}),
    clearButtonLabel: t('actions.delete'),
  };
  const inputFormat = t('components.EDateInput.inputFormat');

  // Emulate the native input event so that it can be seamlessly integrated into forms
  const onChangeHandler: DateFieldProps<moment.Moment>['onChange'] = (
    date,
    context
  ) => {
    const clonedEvent = new Event('input');
    Object.defineProperty(clonedEvent, 'target', {
      writable: true,
      value: {value: convertToISODateString(date), name},
    });
    onChange?.(clonedEvent as unknown as React.ChangeEvent<HTMLInputElement>);
    onDateChanged?.({
      date,
      isValid: !context.validationError,
    });
  };

  // Emulate the native blur event so that it can be seamlessly integrated into forms
  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      // Convert the onBlur value (localized date) to ISO date string
      e.target.value = e.target.value
        ? convertToISODateString(moment(e.target.value, inputFormat))
        : '';
      onBlur(e);
    }
  };

  const commonProps = {
    disabled,
    maxDate: convertToMoment(maxDate) ?? undefined,
    minDate: convertToMoment(minDate) ?? undefined,
    onChange: onChangeHandler,
    value: value ? convertToMoment(value) : dateValue,
    format: inputFormat,
    slots: {
      textField: StyledDateTimeTextField as typeof TextField,
    },
  };

  const commonSlotProps: DatePickerSlotsComponentsProps<Moment> = {
    textField: {
      id: name,
      name,
      autoFocus,
      disabled,
      error,
      inputRef,
      onBlur: onBlurHandler,
      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(e);
      },
      inputProps: {
        'aria-label': ariaLabel,
        'data-testid': `EDateInput-${name}`,
        placeholder: t('components.EDateInput.placeholder'),
        onKeyDown: stopKeyboardEventPropagation,
      },
    },
  };

  return (
    <FormInputWrapper
      bottomText={bottomText}
      error={error}
      errorMessage={errorMessage}
      label={label}
      labelInfo={labelInfo}
      labelProps={{htmlFor: name}}
    >
      {/* note: this div is needed so that the end adornment has the correct height placement when bottomText or error exists */}
      <div style={{position: 'relative'}}>
        <LocalizationProvider
          adapterLocale={moment.locale(lang)}
          dateAdapter={AdapterMoment}
          localeText={localeTexts}
        >
          {inputOnly ? (
            <DateField {...commonProps} slotProps={commonSlotProps} />
          ) : (
            <DatePicker
              {...commonProps}
              dayOfWeekFormatter={(day) => day.slice(0, 2)}
              slotProps={{
                actionBar: {actions},
                ...commonSlotProps,
              }}
            />
          )}
        </LocalizationProvider>
      </div>
    </FormInputWrapper>
  );
};
